import { Injectable, OnDestroy } from '@angular/core';
import { Socket, io } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '@env/environment';

interface SocketOptions {
  transports: string[];
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
}

export interface SocketData {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SocketSwitchService implements OnDestroy {
  private readonly socket: Socket;
  private isConnected = false;
  private readonly destroySubject = new Subject<void>();

  private readonly dataSubject = new BehaviorSubject<SocketData | null>(null);
  public readonly data$ = this.dataSubject.asObservable();

  private readonly forceLogoutSubject = new Subject<{ user_id: number }>();
  public readonly forceLogout$ = this.forceLogoutSubject.asObservable();

  constructor() {
    const socketOptions: SocketOptions = {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    };

    this.socket = io(environment.SWITCH_SOCKET_ENDPOINT, socketOptions);
    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    if (this.isConnected) {
      return;
    }

    this.socket.on('connect', this.handleConnect.bind(this));
    this.socket.on('data', this.handleData.bind(this));
    this.socket.on('force_logout', this.handleForceLogout.bind(this));
    this.socket.on('disconnect', this.handleDisconnect.bind(this));
    this.socket.on('connect_error', this.handleError.bind(this));
    this.socket.on('reconnect_attempt', this.handleReconnectAttempt.bind(this));

    this.isConnected = true;
  }

  public connect(): void {
    if (this.isConnected) {
      console.warn('Socket is already connected');
      return;
    }

    this.socket.connect();
    this.setupSocketListeners();
  }

  public getDataEvent(): Observable<SocketData> {
    const observable = new Observable<SocketData>((observer) => {
      const dataHandler = (data: SocketData) => {
        observer.next(data);
      };

      this.socket.on('data', dataHandler);

      return () => {
        this.socket.off('data', dataHandler);
      };
    });

    return observable.pipe(takeUntil(this.destroySubject));
  }

  public disconnect(): void {
    if (!this.socket) {
      return;
    }

    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.isConnected = false;
    console.log('Socket disconnected');
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.disconnect();
  }

  private handleConnect(): void {
    console.log('Connected to the WebSocket server');
    this.isConnected = true;
  }

  private handleData(data: SocketData): void {
    this.dataSubject.next(data);
  }

  private handleForceLogout(data: { user_id: number }): void {
    this.forceLogoutSubject.next(data);
  }

  private handleDisconnect(): void {
    console.log('Disconnected from the WebSocket server');
    this.isConnected = false;
  }

  private handleError(error: Error): void {
    console.error('Connection error:', error.message);
  }

  private handleReconnectAttempt(attemptNumber: number): void {
    console.log(`Reconnection attempt #${attemptNumber}`);
  }
}
